import { MdFlight, MdEdit, MdDelete, MdVisibility, MdLocationOn, MdSchedule } from 'react-icons/md'
import { Button } from '../ui'

function AirportCard({ airport, onView, onEdit, onDelete }) {
  const getTypeColor = (type) => {
    switch (type) {
      case 'international':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
      case 'domestic':
        return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
      case 'regional':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const getStatusColor = (isActive) => {
    return isActive
      ? 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
  }

  const formatElevation = (elevation) => {
    if (!elevation) return 'N/A'
    return `${elevation.toLocaleString()} ft`
  }

  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return 'N/A'
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
            <MdFlight className="text-primary-600 dark:text-primary-400 text-xl" />
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-white">{airport.name}</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">{airport.code}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Button variant="ghost" size="sm" onClick={onView} className="p-1" title="View Details">
            <MdVisibility className="text-lg" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} className="p-1" title="Edit Airport">
            <MdEdit className="text-lg" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Delete Airport"
          >
            <MdDelete className="text-lg" />
          </Button>
        </div>
      </div>

      {/* Status and Type */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(airport.isActive)}`}
          >
            {airport.isActive ? 'Active' : 'Inactive'}
          </span>
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(airport.type)}`}
          >
            {airport.type?.charAt(0).toUpperCase() + airport.type?.slice(1)}
          </span>
        </div>
      </div>

      {/* Location */}
      <div className="flex items-center space-x-2 mb-4">
        <MdLocationOn className="text-neutral-500 dark:text-neutral-400" />
        <div className="text-sm">
          <p className="text-neutral-900 dark:text-white font-medium">
            {airport.city}, {airport.stateCode || airport.state}
          </p>
          <p className="text-neutral-600 dark:text-neutral-400">{airport.country}</p>
        </div>
      </div>

      {/* Key Information */}
      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Coordinates:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {formatCoordinates(airport.latitude, airport.longitude)}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Elevation:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {formatElevation(airport.elevation)}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Timezone:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {airport.timezone || 'N/A'}
          </span>
        </div>
      </div>

      {/* Runways */}
      {airport.runways && airport.runways.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-neutral-600 dark:text-neutral-400">Runways:</span>
            <span className="text-neutral-900 dark:text-white font-medium">
              {airport.runways.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {airport.runways.slice(0, 3).map((runway, index) => (
              <span
                key={runway._id || index}
                className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded"
                title={`${runway.number} - ${runway.length}m x ${runway.width}m - ${runway.surface}`}
              >
                {runway.number}
              </span>
            ))}
            {airport.runways.length > 3 && (
              <span className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded">
                +{airport.runways.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 pt-4 border-t border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center space-x-1">
          <MdSchedule className="text-sm" />
          <span>Updated: {formatDate(airport.updatedAt)}</span>
        </div>
      </div>
    </div>
  )
}

export default AirportCard
